/**
 * Birne — Moench "Birnhard" (Faust-Wirbel). LMAO-Style (BATTLE-14/17):
 * friedlicher Zen-Moench — klassische Birnen-Silhouette (breiter runder Bauch,
 * schmaler Hals, aufrecht zentriert = Balance), hell-gelbgruener Koerper mit
 * ruhigen, aufgeraeumten Flaechen. Brauner Stiel + ein Blatt oben; Moenchs-
 * Schaerpe (accent-Braun) mit Seitenknoten + haengenden Enden um die Taille.
 * 2★: gewebte Borte auf der Schaerpe + Holzperlen-Mala links am Guertel.
 * 3★: leuchtender Chakra-Punkt auf der Stirn (pulsiert mit castPulse).
 * castPulse laesst zusaetzlich ein sanftes Chi-Gluehen im Bauch aufleben.
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, specular, volumeGradient } from '../draw3d'

export const paintBirne: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)

  // --- Birnen-Silhouette: breiter runder Bauch, schmaler Hals — aufrecht, symmetrisch ---
  const pearPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(0, -r * 1.02)
    ctx.bezierCurveTo(r * 0.34, -r * 1.0, r * 0.46, -r * 0.74, r * 0.44, -r * 0.5)
    ctx.bezierCurveTo(r * 0.6, -r * 0.3, r * 0.82, -r * 0.08, r * 0.82, r * 0.3)
    ctx.bezierCurveTo(r * 0.82, r * 0.72, r * 0.46, r * 0.98, 0, r * 0.98)
    ctx.bezierCurveTo(-r * 0.46, r * 0.98, -r * 0.82, r * 0.72, -r * 0.82, r * 0.3)
    ctx.bezierCurveTo(-r * 0.82, -r * 0.08, -r * 0.6, -r * 0.3, -r * 0.44, -r * 0.5)
    ctx.bezierCurveTo(-r * 0.46, -r * 0.74, -r * 0.34, -r * 1.0, 0, -r * 1.02)
    ctx.closePath()
  }

  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  pearPath()
  // Volumen-Zentrum leicht nach unten: die Masse sitzt im Bauch
  ctx.fillStyle = volumeGradient(ctx, 0, r * 0.12, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // --- Innenleben, geclippt auf die Birne (Silhouette bleibt sauber = Zen) ---
  ctx.save()
  pearPath()
  ctx.clip()

  // wenige, dezente Lentizellen (Birnen-Sprenkel) — Flaechen bleiben ruhig
  ctx.fillStyle = rgba(darken(colors.body, 20), dead ? 0.12 : 0.2)
  for (const [sx, sy, ss] of [
    [0.5, 0.04, 1], [0.62, 0.26, 0.8], [-0.56, 0.12, 0.9], [-0.64, 0.32, 0.7],
  ] as const) {
    ctx.beginPath()
    ctx.arc(sx * r, sy * r, r * 0.035 * ss, 0, Math.PI * 2)
    ctx.fill()
  }

  // sanftes Chi-Gluehen im Bauch beim Cast (Keyword-neutraler warmer Koerperton)
  if (!dead && pose.castPulse > 0) {
    glow(ctx, 0, r * 0.34, r * 0.72, lighten(colors.body, 32), pose.castPulse * 0.24)
  }

  // --- Moenchs-Schaerpe (accent-Braun) um die Taille, unterhalb des Gesichts ---
  const beltY = r * 0.5
  const bg = ctx.createLinearGradient(0, beltY - r * 0.13, 0, beltY + r * 0.13)
  bg.addColorStop(0, lighten(colors.accent, 18))
  bg.addColorStop(0.5, colors.accent)
  bg.addColorStop(1, darken(colors.accent, 16))
  ctx.fillStyle = bg
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.4, r * 0.05)
  ctx.beginPath()
  ctx.ellipse(0, beltY, r * 0.88, r * 0.12, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  // Licht-Kante entlang der Schaerpen-Oberkante (Licht oben-links)
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.accent, 34), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.032)
    ctx.beginPath()
    ctx.ellipse(0, beltY, r * 0.86, r * 0.105, 0, Math.PI * 1.1, Math.PI * 1.9)
    ctx.stroke()
  }

  // 2★: gewebte Borte — zwei helle Ziernaehte + Diagonal-Stiche
  if (tier >= 2) {
    ctx.strokeStyle = rgba(lighten(colors.accent, 38), dead ? 0.35 : 0.85)
    ctx.lineWidth = Math.max(1, r * 0.026)
    for (const t of [-0.52, -0.26, 0, 0.26]) {
      ctx.beginPath()
      ctx.moveTo(t * r - r * 0.035, beltY + r * 0.045)
      ctx.lineTo(t * r + r * 0.035, beltY - r * 0.045)
      ctx.stroke()
    }
  }

  // Knoten der Schaerpe rechts (Kampf-Moench-Motiv) + zwei haengende Enden
  const kx = r * 0.48
  const ky = beltY
  ctx.lineCap = 'round'
  const tail = (x2: number, y2: number, cx2: number): void => {
    ctx.beginPath()
    ctx.moveTo(kx, ky + r * 0.04)
    ctx.quadraticCurveTo(cx2, (ky + y2) / 2 + r * 0.06, x2, y2)
  }
  for (const [tx, ty, tcx] of [[0.3, 0.84, 0.34], [0.6, 0.78, 0.62]] as const) {
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(2, r * 0.12)
    tail(tx * r, ty * r, tcx * r)
    ctx.stroke()
    ctx.strokeStyle = darken(colors.accent, 8)
    ctx.lineWidth = Math.max(1.3, r * 0.075)
    ctx.stroke()
    // Licht-Faden auf dem Stoffende
    ctx.strokeStyle = rgba(lighten(colors.accent, 30), dead ? 0.25 : 0.75)
    ctx.lineWidth = Math.max(1, r * 0.028)
    tail(tx * r - r * 0.015, ty * r - r * 0.04, tcx * r - r * 0.02)
    ctx.stroke()
  }
  // Knoten-Bommel mit eigenem Volumen + Wickel-Falte
  const kg = ctx.createRadialGradient(kx - r * 0.045, ky - r * 0.05, r * 0.02, kx, ky, r * 0.14)
  kg.addColorStop(0, lighten(colors.accent, 26))
  kg.addColorStop(1, darken(colors.accent, 16))
  ctx.fillStyle = kg
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.4, r * 0.05)
  ctx.beginPath()
  ctx.ellipse(kx, ky, r * 0.115, r * 0.1, 0.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.strokeStyle = rgba(darken(colors.accent, 30), 0.75)
  ctx.lineWidth = Math.max(1, r * 0.028)
  ctx.beginPath()
  ctx.moveTo(kx - r * 0.05, ky - r * 0.07)
  ctx.quadraticCurveTo(kx + r * 0.02, ky, kx - r * 0.035, ky + r * 0.07)
  ctx.stroke()

  // 2★: Holzperlen-Mala haengt links vom Guertel (Gebetskette)
  if (tier >= 2) {
    ctx.strokeStyle = rgba(colors.outline, 0.85)
    ctx.lineWidth = Math.max(1, r * 0.025)
    ctx.beginPath()
    ctx.moveTo(-r * 0.58, beltY + r * 0.06)
    ctx.quadraticCurveTo(-r * 0.46, r * 0.84, -r * 0.22, r * 0.82)
    ctx.stroke()
    for (const [bx, by] of [[-0.57, 0.63], [-0.5, 0.73], [-0.4, 0.79], [-0.29, 0.82]] as const) {
      ctx.fillStyle = lighten(colors.accent, dead ? 14 : 30)
      ctx.strokeStyle = colors.outline
      ctx.lineWidth = Math.max(1, r * 0.03)
      ctx.beginPath()
      ctx.arc(bx * r, by * r, r * 0.058, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
      if (!dead) {
        ctx.fillStyle = 'rgba(255, 252, 240, 0.6)'
        ctx.beginPath()
        ctx.arc(bx * r - r * 0.018, by * r - r * 0.02, r * 0.016, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Readability der Silhouette)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  pearPath()
  ctx.stroke()

  // --- Stiel (accent-Braun) mit Outline-Unterlage + Licht-Kante, sitzt am Halsansatz ---
  ctx.lineCap = 'round'
  const stemPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.01, -r * 0.92)
    ctx.quadraticCurveTo(r * 0.02, -r * 1.16, r * 0.17, -r * 1.28)
  }
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2.4, r * 0.16)
  stemPath()
  ctx.stroke()
  ctx.strokeStyle = darken(colors.accent, 6)
  ctx.lineWidth = Math.max(1.5, r * 0.1)
  ctx.stroke()
  ctx.strokeStyle = rgba(lighten(colors.accent, 32), dead ? 0.3 : 0.8)
  ctx.lineWidth = Math.max(1, r * 0.035)
  ctx.beginPath()
  ctx.moveTo(-r * 0.005, -r * 0.97)
  ctx.quadraticCurveTo(r * 0.015, -r * 1.14, r * 0.13, -r * 1.24)
  ctx.stroke()
  // kleine Ansatz-Mulde, wo der Stiel aus der Frucht kommt
  ctx.fillStyle = rgba(darken(colors.body, 24), 0.55)
  ctx.beginPath()
  ctx.ellipse(0, -r * 0.94, r * 0.09, r * 0.045, 0, 0, Math.PI * 2)
  ctx.fill()

  // --- Ein Blatt am Stielansatz, haengt ruhig nach links ---
  ctx.save()
  ctx.translate(-r * 0.04, -r * 1.04)
  ctx.rotate(-0.5)
  const L = r * 0.48
  const lg = ctx.createLinearGradient(-L, -L * 0.3, 0, 0)
  lg.addColorStop(0, lighten(colors.bodyDark, 24))
  lg.addColorStop(1, darken(colors.bodyDark, 8))
  ctx.fillStyle = lg
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.5, r * 0.055)
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.quadraticCurveTo(-L * 0.5, -L * 0.46, -L, -L * 0.16)
  ctx.quadraticCurveTo(-L * 0.46, L * 0.14, 0, 0)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  // Blattader (Licht)
  ctx.strokeStyle = rgba(lighten(colors.bodyDark, 32), dead ? 0.3 : 0.8)
  ctx.lineWidth = Math.max(1, r * 0.028)
  ctx.beginPath()
  ctx.moveTo(-L * 0.12, -L * 0.06)
  ctx.quadraticCurveTo(-L * 0.5, -L * 0.24, -L * 0.82, -L * 0.16)
  ctx.stroke()
  ctx.restore()

  // --- 3★: leuchtender Chakra-Punkt auf der Stirn + zwei stille Chi-Boegen ---
  if (tier >= 3 && !dead) {
    const pulse = 0.85 + pose.castPulse * 0.15
    const chi = lighten(colors.body, 46)
    // dunkler Sockel, damit der helle Punkt auch auf hellem Body traegt
    ctx.fillStyle = rgba(darken(colors.body, 36), 0.9)
    ctx.beginPath()
    ctx.arc(0, -r * 0.66, r * 0.1, 0, Math.PI * 2)
    ctx.fill()
    ctx.save()
    ctx.shadowColor = chi
    ctx.shadowBlur = r * (0.2 + pose.castPulse * 0.18)
    ctx.fillStyle = rgba(chi, pulse)
    ctx.beginPath()
    ctx.arc(0, -r * 0.66, r * 0.062, 0, Math.PI * 2)
    ctx.fill()
    // zwei ruhige Boegen, die den Punkt rahmen (Zen statt Zacken)
    ctx.strokeStyle = rgba(chi, pulse * 0.9)
    ctx.lineWidth = Math.max(1.2, r * 0.04)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.arc(0, -r * 0.66, r * 0.16, Math.PI * 0.72, Math.PI * 1.38)
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(0, -r * 0.66, r * 0.16, Math.PI * 1.62, Math.PI * 2.28)
    ctx.stroke()
    ctx.restore()
  }

  // --- Licht: Rim entlang der linken Birnen-Kontur (oben-links) + Speculars ---
  if (!dead) {
    ctx.strokeStyle = 'rgba(255, 252, 240, 0.5)'
    ctx.lineWidth = Math.max(1.5, r * 0.07)
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(-r * 0.7, r * 0.42)
    ctx.bezierCurveTo(-r * 0.74, r * 0.02, -r * 0.56, -r * 0.26, -r * 0.4, -r * 0.44)
    ctx.bezierCurveTo(-r * 0.42, -r * 0.64, -r * 0.3, -r * 0.86, -r * 0.08, -r * 0.92)
    ctx.stroke()
    // Frucht-Glanz: gross auf der Bauchschulter links, klein am Hals
    specular(ctx, -r * 0.44, -r * 0.06, r * 0.13, 0.42)
    specular(ctx, -r * 0.16, -r * 0.76, r * 0.08, 0.4)
  }

  ctx.restore()
}
